import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Play,
  X,
  User,
  Lock,
  Calendar,
  Terminal,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Info,
  Scissors,
  Percent,
  RotateCw,
} from 'lucide-react';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useCalculatorStatus } from '../../hooks/useCalculatorStatus';
import { useAuth } from '../../context/AuthContext';
import { getAuthToken } from '../../services/authService';
import CopySnackbar from './CopySnackbar';

const FUNCTION_CALCULATOR_TRIGGER_URL =
  process.env.REACT_APP_CALCULATOR_TRIGGER_URL ||
  '/api/calculator/run';

const RATIO_PRESETS = [
  { label: '1:2 Split (2x)', value: '2' },
  { label: '1:5 Split (5x)', value: '5' },
  { label: '1:10 Split (10x)', value: '10' },
  { label: '1:1 Bonus (2x)', value: '2' },
];

const toInputDate = (date: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const year = parsedDate.getFullYear();
  const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsedDate.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type CalcTab = 'daily' | 'split';

const CalculatorTriggerButton: React.FC = () => {
  const { isAdmin, token } = useAuth();

  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CalcTab>('daily');
  const [isTriggeringCalculator, setIsTriggeringCalculator] = useState(false);

  const { selectedDate, refreshData } = useAnalyticsData();
  const {
    status: calcStatus,
    loading: statusLoading,
    isRunning,
    refetch: refetchStatus,
    setOptimisticRunning,
  } = useCalculatorStatus({
    enabled: isAdmin,
    activePollIntervalMs: 2500,
    idlePollIntervalMs: isCalculatorModalOpen ? 5000 : 120000,
    onCompleted: () => {
      void refreshData();
      setToastMessage('Calculator run completed! Fresh market analytics loaded.');
      setCalculatorToastOpen(true);
      setCalculatorToastKey((c) => c + 1);
    },
  });
  const [calculatorUsername, setCalculatorUsername] = useState('');
  const [calculatorPassword, setCalculatorPassword] = useState('');
  
  // Daily pipeline state
  const [calculatorRunDate, setCalculatorRunDate] = useState('');
  const [shouldRestoreBackup, setShouldRestoreBackup] = useState(false);
  const [shouldCreateBackup, setShouldCreateBackup] = useState(false);
  const [shouldBackfillIndexHistory, setShouldBackfillIndexHistory] = useState(false);
  const [indexHistoryBackfillDays, setIndexHistoryBackfillDays] = useState('60');
  const [shouldSkipBhavcopy, setShouldSkipBhavcopy] = useState(false);

  // Stock split adjustment state
  const [splitSymbol, setSplitSymbol] = useState('');
  const [splitDate, setSplitDate] = useState('');
  const [splitRatio, setSplitRatio] = useState('2');
  const [splitCreateBackup, setSplitCreateBackup] = useState(true);

  // Status & feedback
  const [calculatorTriggerMessage, setCalculatorTriggerMessage] = useState('');
  const [calculatorToastOpen, setCalculatorToastOpen] = useState(false);
  const [calculatorToastKey, setCalculatorToastKey] = useState(0);
  const [toastMessage, setToastMessage] = useState('Calculator started successfully.');
  const [copiedUrl, setCopiedUrl] = useState(false);

  const calculatorTriggerUrl = useMemo(() => {
    const argumentsToPass: string[] = [];

    if (activeTab === 'split') {
      argumentsToPass.push('--fix-stock-split');

      const cleanSymbol = splitSymbol.trim().toUpperCase();
      if (cleanSymbol) {
        argumentsToPass.push(`--symbol=${cleanSymbol}`);
      }

      if (splitDate) {
        argumentsToPass.push(`--split-date=${splitDate}`);
      }

      const parsedRatio = parseFloat(splitRatio);
      if (!isNaN(parsedRatio) && parsedRatio > 0) {
        argumentsToPass.push(`--ratio=${parsedRatio}`);
      }

      if (!splitCreateBackup) {
        argumentsToPass.push('--skip-backup');
      }

      const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const triggerUrl = new URL(FUNCTION_CALCULATOR_TRIGGER_URL, origin);
      triggerUrl.searchParams.set('arguments', argumentsToPass.join(' '));
      return FUNCTION_CALCULATOR_TRIGGER_URL.startsWith('http')
        ? triggerUrl.toString()
        : `${triggerUrl.pathname}${triggerUrl.search}`;
    }

    // Daily tab arguments
    if (shouldRestoreBackup) {
      argumentsToPass.push('--restore-backup');
    }

    if (calculatorRunDate) {
      argumentsToPass.push(`--date=${calculatorRunDate}`);
    }

    if (!shouldCreateBackup) {
      argumentsToPass.push('--skip-backup');
    }

    if (shouldBackfillIndexHistory) {
      const parsedDays = Number.parseInt(indexHistoryBackfillDays, 10);
      argumentsToPass.push(`--backfill-index-history-days=${Number.isFinite(parsedDays) && parsedDays > 0 ? parsedDays : 60}`);
    } else if (shouldSkipBhavcopy) {
      argumentsToPass.push('--skip-bhavcopy');
    }

    if (argumentsToPass.length === 0) {
      return FUNCTION_CALCULATOR_TRIGGER_URL;
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const triggerUrl = new URL(FUNCTION_CALCULATOR_TRIGGER_URL, origin);
    triggerUrl.searchParams.set('arguments', argumentsToPass.join(' '));
    return FUNCTION_CALCULATOR_TRIGGER_URL.startsWith('http')
      ? triggerUrl.toString()
      : `${triggerUrl.pathname}${triggerUrl.search}`;
  }, [
    activeTab,
    splitSymbol,
    splitDate,
    splitRatio,
    splitCreateBackup,
    calculatorRunDate,
    shouldCreateBackup,
    shouldRestoreBackup,
    shouldBackfillIndexHistory,
    indexHistoryBackfillDays,
    shouldSkipBhavcopy,
  ]);

  const closeCalculatorModal = useCallback(() => {
    if (isTriggeringCalculator) {
      return;
    }

    setIsCalculatorModalOpen(false);
    setCalculatorPassword('');
    setCalculatorRunDate('');
    setSplitSymbol('');
    setSplitDate('');
    setSplitRatio('2');
    setSplitCreateBackup(true);
    setShouldRestoreBackup(false);
    setShouldCreateBackup(false);
    setShouldBackfillIndexHistory(false);
    setIndexHistoryBackfillDays('60');
    setShouldSkipBhavcopy(false);
    setCopiedUrl(false);
  }, [isTriggeringCalculator]);

  // Handle Esc key to close modal
  useEffect(() => {
    if (!isCalculatorModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isTriggeringCalculator) {
        closeCalculatorModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCalculatorModalOpen, isTriggeringCalculator, closeCalculatorModal]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(calculatorTriggerUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      // fallback if clipboard API not permitted
    }
  };

  const handleRunCalculator = async () => {
    if (activeTab === 'split') {
      const cleanSymbol = splitSymbol.trim().toUpperCase();
      if (!cleanSymbol) {
        setCalculatorTriggerMessage('Enter a valid stock symbol (e.g. TCS, EICHERMOT).');
        return;
      }
      if (!splitDate) {
        setCalculatorTriggerMessage('Select the Split / Bonus Ex-Date.');
        return;
      }
      const parsedRatio = parseFloat(splitRatio);
      if (isNaN(parsedRatio) || parsedRatio <= 0 || Math.abs(parsedRatio - 1.0) < 0.0001) {
        setCalculatorTriggerMessage('Enter a valid split ratio (> 0 and not 1.0, e.g. 2 for 1:2 split, 10 for 1:10 split).');
        return;
      }
    }

    try {
      setIsTriggeringCalculator(true);
      const cleanSymbol = splitSymbol.trim().toUpperCase();
      setCalculatorTriggerMessage(
        activeTab === 'split'
          ? `Triggering isolated stock split adjustment for ${cleanSymbol}...`
          : 'Triggering serverless accumulation calculator...'
      );

      const payload: Record<string, any> = {};
      if (activeTab === 'split') {
        payload.mode = 'split';
        payload.symbol = cleanSymbol;
        payload.splitDate = splitDate;
        payload.ratio = parseFloat(splitRatio);
        payload.createBackup = splitCreateBackup;
      } else {
        payload.mode = 'daily';
        if (calculatorRunDate) payload.date = calculatorRunDate;
        payload.createBackup = shouldCreateBackup;
        payload.restoreBackup = shouldRestoreBackup;
        payload.skipBhavcopy = shouldSkipBhavcopy;
        if (shouldBackfillIndexHistory) {
          const parsedDays = Number.parseInt(indexHistoryBackfillDays, 10);
          if (Number.isFinite(parsedDays) && parsedDays > 0) {
            payload.backfillDays = parsedDays;
          }
        }
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const freshToken = token || (await getAuthToken());
      if (freshToken) {
        headers['Authorization'] = `Bearer ${freshToken}`;
        headers['X-Authorization'] = `Bearer ${freshToken}`;
      }
      if (calculatorUsername.trim() && calculatorPassword) {
        const basicToken = window.btoa(
          unescape(encodeURIComponent(`${calculatorUsername.trim()}:${calculatorPassword}`))
        );
        headers['Authorization'] = `Basic ${basicToken}`;
        headers['X-Authorization'] = `Basic ${basicToken}`;
      }

      const response = await fetch(calculatorTriggerUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}${errorText ? `: ${errorText}` : ''}`);
      }

      // Optimistically transition status card and topbar pill immediately
      const initialPhase =
        activeTab === 'split'
          ? `Stock split queued for ${cleanSymbol}. Starting worker...`
          : shouldSkipBhavcopy
          ? 'Index update job queued. Starting worker...'
          : 'Job queued on fa-niftysutra. Starting serverless worker...';
      setOptimisticRunning(initialPhase);

      setToastMessage(
        activeTab === 'split'
          ? `Stock split fix job queued for ${cleanSymbol}.`
          : shouldSkipBhavcopy
          ? 'Index update job queued! Tracking live progress...'
          : 'Calculator queued! Tracking live progress...'
      );
      setCalculatorTriggerMessage(
        activeTab === 'split'
          ? `Stock split fix queued for ${cleanSymbol}. Tracking status...`
          : shouldSkipBhavcopy
          ? 'Index update job queued. Tracking live progress below...'
          : 'Calculation job queued. Tracking live progress below...'
      );
      setCalculatorToastOpen(true);
      setCalculatorToastKey((current) => current + 1);
    } catch (triggerError) {
      console.error('Failed to trigger calculator:', triggerError);
      setCalculatorTriggerMessage(
        `Trigger failed: ${triggerError instanceof Error ? triggerError.message : 'Unknown error'}`
      );
    } finally {
      setIsTriggeringCalculator(false);
    }
  };

  const openCalculatorModal = () => {
    void refetchStatus();
    setCalculatorTriggerMessage('');
    setCalculatorRunDate('');
    setSplitDate(toInputDate(selectedDate));
    setShouldRestoreBackup(false);
    setShouldCreateBackup(false);
    setShouldBackfillIndexHistory(false);
    setIndexHistoryBackfillDays('60');
    setShouldSkipBhavcopy(false);
    setCopiedUrl(false);
    setIsCalculatorModalOpen(true);
  };

  const isErrorMessage =
    calculatorTriggerMessage &&
    (calculatorTriggerMessage.toLowerCase().includes('failed') ||
      calculatorTriggerMessage.toLowerCase().includes('blocking') ||
      calculatorTriggerMessage.toLowerCase().includes('enter') ||
      calculatorTriggerMessage.toLowerCase().includes('select'));

  const calculatorModal = isCalculatorModalOpen ? (
    <div
      className="ns-calc-modal__overlay"
      onClick={closeCalculatorModal}
      role="presentation"
    >
      <div
        className="ns-calc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calculator-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ns-calc-modal__header">
          <div className="ns-calc-modal__header-info">
            <div className="ns-calc-modal__eyebrow">
              {activeTab === 'split' ? (
                <>
                  <Scissors size={12} className="ns-calc-modal__eyebrow-icon" />
                  <span>HISTORY RECTIFICATION</span>
                </>
              ) : (
                <>
                  <Terminal size={12} className="ns-calc-modal__eyebrow-icon" />
                  <span>MANUAL TRIGGER</span>
                </>
              )}
            </div>
            <h2 id="calculator-modal-title" className="ns-calc-modal__title">
              {activeTab === 'split'
                ? 'Fix stock split / bonus history'
                : 'Run accumulation calculator'}
            </h2>
            <p className="ns-calc-modal__subtitle">
              {activeTab === 'split'
                ? 'Recalculate historical prices, volumes, and technical indicators for a single stock after a corporate action without running the full market pipeline.'
                : 'Trigger serverless accumulation calculations and update market analytics.'}
            </p>
          </div>
          <button
            type="button"
            className="ns-calc-modal__close-btn"
            onClick={closeCalculatorModal}
            disabled={isTriggeringCalculator}
            aria-label="Close modal (Esc)"
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="ns-calc-modal__body">
          {/* Live WebJob Status & Monitor Card */}
          <div className={`ns-calc-status-card ns-calc-status-card--${calcStatus?.status?.toLowerCase() || 'idle'}`}>
            <div className="ns-calc-status-card__header">
              <div className="ns-calc-status-card__indicator">
                <span className={`ns-calc-status-dot ns-calc-status-dot--${calcStatus?.status?.toLowerCase() || 'idle'}`} />
                <span className="ns-calc-status-card__badge-text">
                  {isRunning
                    ? 'CALCULATOR RUNNING'
                    : calcStatus?.status === 'Completed'
                    ? 'IDLE (LAST RUN COMPLETE)'
                    : calcStatus?.status === 'Failed'
                    ? 'LAST RUN FAILED'
                    : 'READY TO RUN'}
                </span>
              </div>
              <div className="ns-calc-status-card__controls">
                {calcStatus?.lastHeartbeat && (
                  <span className="ns-calc-status-card__time" title="Last update">
                    {new Date(calcStatus.lastHeartbeat).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
                <button
                  type="button"
                  className="ns-calc-status-card__refresh-btn"
                  onClick={() => refetchStatus()}
                  disabled={statusLoading}
                  title="Check latest status"
                >
                  <RotateCw size={12} className={statusLoading ? 'ns-spin' : ''} />
                </button>
              </div>
            </div>

            {isRunning && (
              <div className="ns-calc-status-card__live">
                <div className="ns-calc-status-card__progress-wrap">
                  <div className="ns-calc-status-card__progress-info">
                    <span className="ns-calc-status-card__phase">{calcStatus?.phase || 'Processing market stocks...'}</span>
                    <span className="ns-calc-status-card__percent">{calcStatus?.percentComplete ?? 0}%</span>
                  </div>
                  <div className="ns-calc-status-card__bar-track">
                    <div
                      className="ns-calc-status-card__bar-fill"
                      style={{ width: `${Math.min(100, Math.max(2, calcStatus?.percentComplete ?? 0))}%` }}
                    />
                  </div>
                </div>

                <div className="ns-calc-status-card__metrics">
                  <div className="ns-calc-status-card__metric">
                    <span className="ns-calc-status-card__metric-label">Processed</span>
                    <span className="ns-calc-status-card__metric-val">
                      {(calcStatus?.processedStocks ?? 0).toLocaleString()} / {(calcStatus?.totalStocks ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="ns-calc-status-card__metric">
                    <span className="ns-calc-status-card__metric-label">Elapsed</span>
                    <span className="ns-calc-status-card__metric-val">{calcStatus?.durationSeconds ? `${calcStatus.durationSeconds}s` : '0s'}</span>
                  </div>
                  <div className="ns-calc-status-card__metric">
                    <span className="ns-calc-status-card__metric-label">Speed</span>
                    <span className="ns-calc-status-card__metric-val">
                      {calcStatus?.processingRate ? `${calcStatus.processingRate}/s` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!isRunning && calcStatus?.status === 'Completed' && (
              <div className="ns-calc-status-card__summary">
                <div className="ns-calc-status-card__summary-text">
                  <Check size={14} className="ns-calc-status-card__check-icon" />
                  <span>
                    Last run took <strong>{calcStatus.durationSeconds}s</strong> ({calcStatus.totalStocks?.toLocaleString() || 0} stocks) on {calcStatus.runDate || (calcStatus.completedAt ? new Date(calcStatus.completedAt).toLocaleDateString() : 'today')}.
                  </span>
                </div>
                <button
                  type="button"
                  className="ns-calc-status-card__reload-data-btn"
                  onClick={async () => {
                    await refreshData();
                    setToastMessage('Market analytics refreshed.');
                    setCalculatorToastOpen(true);
                    setCalculatorToastKey((k) => k + 1);
                  }}
                  title="Reload UI data from cloud storage"
                >
                  <RotateCw size={11} />
                  <span>Reload Data</span>
                </button>
              </div>
            )}

            {!isRunning && calcStatus?.status === 'Failed' && (
              <div className="ns-calc-status-card__error">
                <AlertCircle size={14} />
                <span>{calcStatus.errorMessage || 'Execution encountered an unexpected error.'}</span>
              </div>
            )}
          </div>

          {/* Tab Switcher */}
          <div className="ns-calc-modal__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'daily'}
              className={`ns-calc-modal__tab ${activeTab === 'daily' ? 'ns-calc-modal__tab--active' : ''}`}
              onClick={() => {
                setActiveTab('daily');
                setCalculatorTriggerMessage('');
              }}
              disabled={isTriggeringCalculator}
            >
              <Play size={13} fill="currentColor" />
              <span>Daily Pipeline</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'split'}
              className={`ns-calc-modal__tab ${activeTab === 'split' ? 'ns-calc-modal__tab--active' : ''}`}
              onClick={() => {
                setActiveTab('split');
                setCalculatorTriggerMessage('');
              }}
              disabled={isTriggeringCalculator}
            >
              <Scissors size={13} />
              <span>Fix Stock Split</span>
            </button>
          </div>

          {/* Optional Credentials */}
          <details className="ns-calc-modal__details" style={{ marginBottom: '16px' }}>
            <summary style={{ fontSize: '12px', color: 'var(--text-muted, #94a3b8)', cursor: 'pointer', marginBottom: '8px' }}>
              Advanced: Custom deployment credentials (optional)
            </summary>
            <div className="ns-calc-modal__field" style={{ marginTop: '8px' }}>
              <label htmlFor="calculator-username" className="ns-calc-modal__label">
                <User size={13} className="ns-calc-modal__field-icon" />
                <span>Username (Optional)</span>
              </label>
              <input
                id="calculator-username"
                type="text"
                value={calculatorUsername}
                onChange={(e) => setCalculatorUsername(e.target.value)}
                className="ns-calc-modal__input"
                autoComplete="username"
                placeholder="Deployment username (optional)"
                disabled={isTriggeringCalculator}
              />
            </div>

            <div className="ns-calc-modal__field">
              <label htmlFor="calculator-password" className="ns-calc-modal__label">
                <Lock size={13} className="ns-calc-modal__field-icon" />
                <span>Password (Optional)</span>
              </label>
              <input
                id="calculator-password"
                type="password"
                value={calculatorPassword}
                onChange={(e) => setCalculatorPassword(e.target.value)}
                className="ns-calc-modal__input"
                autoComplete="current-password"
                placeholder="Deployment password (optional)"
                disabled={isTriggeringCalculator}
              />
            </div>
          </details>

          {activeTab === 'daily' ? (
            <>
              {/* Run for specific date Field */}
              <div className="ns-calc-modal__field">
                <div className="ns-calc-modal__label-row">
                  <label htmlFor="calculator-run-date" className="ns-calc-modal__label">
                    <Calendar size={13} className="ns-calc-modal__field-icon" />
                    <span>Run for specific date</span>
                  </label>
                  {calculatorRunDate && (
                    <button
                      type="button"
                      className="ns-calc-modal__date-clear-btn"
                      onClick={() => setCalculatorRunDate('')}
                      disabled={isTriggeringCalculator}
                      title="Clear date (defaults to latest bhavcopy)"
                    >
                      Clear (Use Latest)
                    </button>
                  )}
                </div>
                <input
                  id="calculator-run-date"
                  type="date"
                  value={calculatorRunDate}
                  onChange={(e) => setCalculatorRunDate(e.target.value)}
                  className="ns-calc-modal__input"
                  placeholder="yyyy-mm-dd"
                  disabled={isTriggeringCalculator}
                />
                <p className="ns-calc-modal__hint">
                  Leave blank (default) to automatically download and process the latest available NSE bhavcopy, or select a specific session date to re-run (<code className="ns-calc-modal__code-chip">--date</code>).
                </p>
              </div>

              {/* Option toggles */}
              <div className="ns-calc-modal__options-group">
                <label
                  htmlFor="calculator-restore-backup"
                  className={`ns-calc-modal__toggle ${shouldRestoreBackup ? 'ns-calc-modal__toggle--checked' : ''}`}
                >
                  <input
                    id="calculator-restore-backup"
                    type="checkbox"
                    checked={shouldRestoreBackup}
                    onChange={(e) => setShouldRestoreBackup(e.target.checked)}
                    disabled={isTriggeringCalculator}
                    className="ns-calc-modal__checkbox"
                  />
                  <div className="ns-calc-modal__toggle-text">
                    <span className="ns-calc-modal__toggle-title">Restore backup before run</span>
                    <span className="ns-calc-modal__toggle-desc">
                      Passes <code className="ns-calc-modal__code-chip">--restore-backup</code>. If a run date is provided, restore happens first and then the calculator continues for that date.
                    </span>
                  </div>
                </label>

                <label
                  htmlFor="calculator-create-backup"
                  className={`ns-calc-modal__toggle ${shouldCreateBackup ? 'ns-calc-modal__toggle--checked' : ''}`}
                >
                  <input
                    id="calculator-create-backup"
                    type="checkbox"
                    checked={shouldCreateBackup}
                    onChange={(e) => setShouldCreateBackup(e.target.checked)}
                    disabled={isTriggeringCalculator}
                    className="ns-calc-modal__checkbox"
                  />
                  <div className="ns-calc-modal__toggle-text">
                    <span className="ns-calc-modal__toggle-title">Create backup during upload</span>
                    <span className="ns-calc-modal__toggle-desc">
                      Disabled by default. Turn it on only when you want safety backup copies. Otherwise the calculator passes <code className="ns-calc-modal__code-chip">--skip-backup</code>.
                    </span>
                  </div>
                </label>

                <label
                  htmlFor="calculator-backfill-index-history"
                  className={`ns-calc-modal__toggle ${shouldBackfillIndexHistory ? 'ns-calc-modal__toggle--checked' : ''}`}
                >
                  <input
                    id="calculator-backfill-index-history"
                    type="checkbox"
                    checked={shouldBackfillIndexHistory}
                    onChange={(e) => {
                      setShouldBackfillIndexHistory(e.target.checked);
                      if (e.target.checked) {
                        setShouldSkipBhavcopy(false);
                      }
                    }}
                    disabled={isTriggeringCalculator}
                    className="ns-calc-modal__checkbox"
                  />
                  <div className="ns-calc-modal__toggle-text">
                    <span className="ns-calc-modal__toggle-title">Backfill / re-run NSE index history</span>
                    <span className="ns-calc-modal__toggle-desc">
                      Downloads and stores historical OHLC data for every NSE index (Nifty 50, sectoral/thematic indices, etc.) for the last N weekdays, then exits without running the normal daily stock pipeline. Passes <code className="ns-calc-modal__code-chip">--backfill-index-history-days</code>. Set N to <strong>1</strong> (with "Run for specific date" above, if not today) to just (re)process a single day's index file once NSE finally uploads it — useful when the index file is delayed past the bhavcopy.
                    </span>
                    {shouldBackfillIndexHistory && (
                      <div className="ns-calc-modal__inline-field" onClick={(e) => e.stopPropagation()}>
                        <label htmlFor="calculator-backfill-days" className="ns-calc-modal__inline-label">
                          Days to backfill
                        </label>
                        <input
                          id="calculator-backfill-days"
                          type="number"
                          min={1}
                          max={365}
                          value={indexHistoryBackfillDays}
                          onChange={(e) => setIndexHistoryBackfillDays(e.target.value)}
                          disabled={isTriggeringCalculator}
                          className="ns-calc-modal__input ns-calc-modal__input--compact"
                        />
                      </div>
                    )}
                  </div>
                </label>

                <label
                  htmlFor="calculator-skip-bhavcopy"
                  className={`ns-calc-modal__toggle ${shouldSkipBhavcopy ? 'ns-calc-modal__toggle--checked' : ''}`}
                >
                  <input
                    id="calculator-skip-bhavcopy"
                    type="checkbox"
                    checked={shouldSkipBhavcopy}
                    onChange={(e) => {
                      setShouldSkipBhavcopy(e.target.checked);
                      if (e.target.checked) {
                        setShouldBackfillIndexHistory(false);
                      }
                    }}
                    disabled={isTriggeringCalculator || shouldBackfillIndexHistory}
                    className="ns-calc-modal__checkbox"
                  />
                  <div className="ns-calc-modal__toggle-text">
                    <span className="ns-calc-modal__toggle-title">Skip bhavcopy (index only)</span>
                    <span className="ns-calc-modal__toggle-desc">
                      Skips the stock/bhavcopy pipeline entirely and only downloads/processes NSE's index file for the resolved date ("Run for specific date" above, or today). Passes <code className="ns-calc-modal__code-chip">--skip-bhavcopy</code>. The two pipelines are independent — on a normal run (neither toggle checked), a missing/delayed index file never blocks the bhavcopy/stock/analytics processing.
                    </span>
                  </div>
                </label>
              </div>
            </>
          ) : (
            <>
              {/* Isolated Split Callout */}
              <div className="ns-calc-modal__callout">
                <Info size={16} className="ns-calc-modal__callout-icon" />
                <div>
                  <strong>Isolated Stock Split Adjustment:</strong> Divides pre-split prices by the ratio, multiplies pre-split volumes by the ratio, and sequentially recalculates 21/50/200 EMAs, rolling 30-day average volume, and % changes. Exits immediately in ~1-2s without downloading bhavcopy or processing other stocks.
                </div>
              </div>

              {/* Stock Symbol */}
              <div className="ns-calc-modal__field">
                <label htmlFor="split-symbol" className="ns-calc-modal__label">
                  <Terminal size={13} className="ns-calc-modal__field-icon" />
                  <span>Stock Symbol</span>
                </label>
                <input
                  id="split-symbol"
                  type="text"
                  value={splitSymbol}
                  onChange={(e) => setSplitSymbol(e.target.value.toUpperCase())}
                  className="ns-calc-modal__input"
                  placeholder="e.g. TCS or EICHERMOT"
                  disabled={isTriggeringCalculator}
                  autoCapitalize="characters"
                />
                <p className="ns-calc-modal__hint">NSE symbol as stored in the system (e.g. TCS, EICHERMOT).</p>
              </div>

              {/* Split Ex-Date */}
              <div className="ns-calc-modal__field">
                <label htmlFor="split-date" className="ns-calc-modal__label">
                  <Calendar size={13} className="ns-calc-modal__field-icon" />
                  <span>Split / Bonus Ex-Date</span>
                </label>
                <input
                  id="split-date"
                  type="date"
                  value={splitDate}
                  onChange={(e) => setSplitDate(e.target.value)}
                  className="ns-calc-modal__input"
                  disabled={isTriggeringCalculator}
                />
                <p className="ns-calc-modal__hint">
                  The first trading day on which the stock traded at the post-split price. All history strictly before this date will be adjusted.
                </p>
              </div>

              {/* Split Ratio */}
              <div className="ns-calc-modal__field">
                <label htmlFor="split-ratio" className="ns-calc-modal__label">
                  <Percent size={13} className="ns-calc-modal__field-icon" />
                  <span>Split Ratio (Factor)</span>
                </label>
                <input
                  id="split-ratio"
                  type="number"
                  step="any"
                  min="0.01"
                  value={splitRatio}
                  onChange={(e) => setSplitRatio(e.target.value)}
                  className="ns-calc-modal__input"
                  placeholder="e.g. 2 for 1:2 split, 10 for 1:10 split"
                  disabled={isTriggeringCalculator}
                />
                <div className="ns-calc-modal__chips-row">
                  {RATIO_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className={`ns-calc-modal__chip ${splitRatio === preset.value ? 'ns-calc-modal__chip--selected' : ''}`}
                      onClick={() => setSplitRatio(preset.value)}
                      disabled={isTriggeringCalculator}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <p className="ns-calc-modal__hint">
                  Old Price / New Price ratio. Example: 1:10 split uses <strong>10</strong> (prices divided by 10, volumes multiplied by 10). 1:1 bonus issue uses <strong>2</strong>.
                </p>
              </div>

              {/* Backup Toggle */}
              <div className="ns-calc-modal__options-group">
                <label
                  htmlFor="split-create-backup"
                  className={`ns-calc-modal__toggle ${splitCreateBackup ? 'ns-calc-modal__toggle--checked' : ''}`}
                >
                  <input
                    id="split-create-backup"
                    type="checkbox"
                    checked={splitCreateBackup}
                    onChange={(e) => setSplitCreateBackup(e.target.checked)}
                    disabled={isTriggeringCalculator}
                    className="ns-calc-modal__checkbox"
                  />
                  <div className="ns-calc-modal__toggle-text">
                    <span className="ns-calc-modal__toggle-title">Create safety backup before modifying</span>
                    <span className="ns-calc-modal__toggle-desc">
                      Recommended. Automatically saves a safety backup of the original unadjusted file in cloud storage before overwriting.
                    </span>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Trigger URL preview card */}
          <div className="ns-calc-modal__endpoint-box">
            <div className="ns-calc-modal__endpoint-head">
              <span className="ns-calc-modal__endpoint-badge">
                <Terminal size={11} />
                <span>TRIGGER ENDPOINT</span>
              </span>
              <button
                type="button"
                className="ns-ghost-copy-btn"
                onClick={handleCopyUrl}
                title={copiedUrl ? 'Copied URL!' : 'Copy trigger URL'}
                aria-label="Copy trigger URL"
              >
                {copiedUrl ? (
                  <Check size={14} color="var(--ns-positive, #10b981)" />
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
            <div className="ns-calc-modal__code-block">
              {calculatorTriggerUrl}
            </div>
          </div>

          {/* Message banner */}
          {calculatorTriggerMessage && (
            <div
              className={`ns-calc-modal__alert ${
                isErrorMessage ? 'ns-calc-modal__alert--error' : 'ns-calc-modal__alert--info'
              }`}
            >
              {isErrorMessage ? (
                <AlertCircle size={15} className="ns-calc-modal__alert-icon" />
              ) : (
                <Info size={15} className="ns-calc-modal__alert-icon" />
              )}
              <span>{calculatorTriggerMessage}</span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="ns-calc-modal__actions" data-html2canvas-ignore="true">
          <button
            type="button"
            className="ns-btn ns-btn--secondary"
            onClick={closeCalculatorModal}
            disabled={isTriggeringCalculator}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ns-btn ns-btn--run"
            onClick={handleRunCalculator}
            disabled={isTriggeringCalculator}
          >
            {isTriggeringCalculator ? (
              <>
                <Loader2 size={14} className="ns-spin" />
                <span>Triggering...</span>
              </>
            ) : activeTab === 'split' ? (
              <>
                <Scissors size={13} />
                <span>Fix {splitSymbol.trim().toUpperCase() || 'Stock'} Split</span>
              </>
            ) : (
              <>
                <Play size={13} fill="currentColor" />
                <span>Run now</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="ns-calc-trigger-wrapper">
      {/* Unified Calculator Action & Status Button */}
      <button
        type="button"
        onClick={openCalculatorModal}
        className={`ns-calc-unified-btn ns-calc-status-pill ns-calc-status-pill--${calcStatus?.status?.toLowerCase() || 'idle'} ${
          isRunning ? 'ns-calc-status-pill--running ns-topbar__run-btn--running' : ''
        }`}
        title={
          isRunning
            ? `Calculator is running: ${calcStatus?.processedStocks || 0}/${calcStatus?.totalStocks || 0} stocks (${calcStatus?.percentComplete || 0}%) — Click to view live progress`
            : calcStatus?.status === 'Completed'
            ? `Calculator Idle (Last run: ${calcStatus?.durationSeconds || 0}s, ${calcStatus?.totalStocks || 0} stocks) — Click to run/view`
            : calcStatus?.status === 'Failed'
            ? 'Calculator Error — Click to view details'
            : 'Accumulation Calculator (Admin) — Click to run'
        }
        aria-label="Accumulation Calculator (Admin)"
      >
        <span className="ns-calc-unified-btn__icon" aria-hidden="true">
          {isRunning ? (
            <Loader2 size={13} className="ns-spin" />
          ) : (
            <Play size={12} fill="currentColor" />
          )}
        </span>
      </button>

      {typeof document !== 'undefined' && calculatorModal
        ? createPortal(calculatorModal, document.body)
        : null}

      <CopySnackbar
        open={calculatorToastOpen}
        onClose={() => setCalculatorToastOpen(false)}
        message={toastMessage}
        instanceKey={calculatorToastKey}
      />
    </div>
  );
};

export default CalculatorTriggerButton;